import { cn } from '@/lib/cn'
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import type { ReactNode } from 'react'

type Tone = 'info' | 'positive' | 'caution' | 'critical'

const TONES: Record<Tone, { wrap: string; icon: ReactNode }> = {
  info: { wrap: 'border-line-strong bg-surface-sunken text-ink-soft', icon: <Info size={15} /> },
  positive: {
    wrap: 'border-positive/30 bg-positive-soft text-positive',
    icon: <CheckCircle2 size={15} />,
  },
  caution: {
    wrap: 'border-caution/35 bg-caution-soft text-caution',
    icon: <AlertTriangle size={15} />,
  },
  critical: {
    wrap: 'border-critical/30 bg-critical-soft text-critical',
    icon: <AlertTriangle size={15} />,
  },
}

/**
 * Static inline banner for a state that persists on the page: a warning, a
 * confirmation, an exclusion reason. Transient messages belong in the toast API
 * instead; form validation belongs in `FormError`.
 */
export function Note({
  tone = 'info',
  className,
  children,
}: {
  tone?: Tone
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-[var(--radius-field)] border px-3.5 py-2.5 text-label',
        TONES[tone].wrap,
        className,
      )}
    >
      <span className="mt-px shrink-0">{TONES[tone].icon}</span>
      <div className="min-w-0 text-pretty">{children}</div>
    </div>
  )
}
